import React from 'react';
import {
  groupPlansForPricingGrid,
  formatPlanPriceDisplay,
  getPlanDisplayCopy,
  developerTierKey,
  tierBadgeLabel,
  classifyPlan,
} from '../../utils/planCatalogUtils';

function PlanFeatures({ plan, extraFeatures = [] }) {
  const features = [
    ...(Array.isArray(plan?.features) ? plan.features : []),
    ...extraFeatures,
  ].filter(Boolean);
  if (!features.length) return null;
  return (
    <ul className="candor-plan-features">
      {features.map((feature) => (
        <li key={feature}>
          <span>✓</span>
          <span>{feature}</span>
        </li>
      ))}
    </ul>
  );
}

function CurrentBadge() {
  return (
    <span className="absolute top-4 right-4 bg-emerald-500 text-white rounded-full p-1.5 shadow">
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      </svg>
    </span>
  );
}

/**
 * Dynamic pricing grid — data from API (is_active, plan_sort).
 */
export default function PricingPlansGrid({
  plansList = [],
  plansLoading = false,
  currentPlanSlug = null,
  subscription = null,
  billingInterval = 'monthly',
  developerTab = 'basic',
  onDeveloperTabChange,
  onPlanSelect,
  onCancelSubscription,
  onContactSales,
  loading = {},
  dashboardCta = true,
}) {
  const grouped = groupPlansForPricingGrid(plansList);
  const { starter, developerTiers, payg, enterprise, other } = grouped;

  const activeDevPlan =
    developerTiers.find((p) => developerTierKey(p) === developerTab) ||
    developerTiers[0] ||
    null;

  const isCurrent = (slug) => currentPlanSlug && slug === currentPlanSlug;

  const isLoadingSlug = (slug) => Boolean(loading[slug] || loading.upgrade || loading.downgrade);

  if (plansLoading) {
    return (
      <div className="candor-pricing-grid">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="candor-plan-card animate-pulse min-h-[320px] bg-slate-50" />
        ))}
      </div>
    );
  }

  if (!plansList.length) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-center text-amber-900">
        <p className="font-semibold">Plans could not be loaded</p>
        <p className="text-sm mt-1">Check the API connection and that active plans exist in the database.</p>
      </div>
    );
  }

  return (
    <div className="candor-pricing-grid">
      {starter && (() => {
        const slug = starter.slug;
        const copy = getPlanDisplayCopy(starter, billingInterval);
        const price = formatPlanPriceDisplay(starter, billingInterval);
        const current = isCurrent(slug);
        return (
          <div
            key={slug}
            className={`candor-plan-card relative ${current ? 'ring-2 ring-blue-500/80 border-blue-500/50' : ''}`}
          >
            {current && <CurrentBadge />}
            <div className="candor-plan-card__body">
              <h3 className="candor-plan-card__title">{starter.name}</h3>
              <p className="candor-plan-card__tagline">
                {starter.description ||
                  'For individual data engineers getting started with local schema-aware AI.'}
              </p>
              <div className="candor-plan-card__price-row">
                <span className="candor-plan-card__price">{price.amount}</span>
                {price.suffix && (
                  <span className="candor-plan-card__price-suffix">{price.suffix}</span>
                )}
              </div>
              {copy.priceNote && <p className="candor-plan-card__desc">{copy.priceNote}</p>}
              {Number(starter.trial_days) > 0 && (
                <p className="candor-plan-card__desc mt-1">
                  {Math.floor(Number(starter.trial_days))}-day Community trial
                </p>
              )}
              <hr className="candor-plan-card__divider" />
              <h4 className="candor-plan-card__includes">{copy.includesLabel}</h4>
              <PlanFeatures
                plan={starter}
                extraFeatures={[
                  starter.daily_token_limit != null
                    ? `${Number(starter.daily_token_limit).toLocaleString()} daily tokens`
                    : null,
                  starter.daily_prompt_limit != null
                    ? `${Number(starter.daily_prompt_limit).toLocaleString()} daily prompts`
                    : null,
                  starter.max_db_connections != null
                    ? `${Number(starter.max_db_connections)} DB connections`
                    : null,
                ].filter(Boolean)}
              />
            </div>
            <div className="candor-plan-card__footer">
              {current ? (
                <div className="w-full text-center py-2 bg-emerald-50 text-emerald-700 font-bold rounded-xl text-xs border border-emerald-100">
                  Current Plan
                </div>
              ) : (
                <button
                  type="button"
                  disabled
                  className="w-full text-center py-2 bg-slate-100 text-slate-400 font-bold rounded-xl text-xs cursor-not-allowed border border-slate-200"
                >
                  Community
                </button>
              )}
            </div>
          </div>
        );
      })()}

      {developerTiers.length > 0 && activeDevPlan && (() => {
        const slug = activeDevPlan.slug;
        const copy = getPlanDisplayCopy(activeDevPlan, billingInterval);
        const price = formatPlanPriceDisplay(activeDevPlan, billingInterval);
        const current = isCurrent(slug);
        const tier = developerTab;
        return (
          <div
            className={`candor-plan-card relative ${
              current
                ? tier === 'basic'
                  ? 'ring-2 ring-blue-500/80 border-blue-500/50'
                  : tier === 'plus'
                    ? 'ring-2 ring-indigo-500/80 border-indigo-500/50'
                    : 'ring-2 ring-purple-500/80 border-purple-500/50'
                : ''
            }`}
          >
            {current && <CurrentBadge />}
            <div className="candor-plan-card__body">
              <div className="flex justify-between items-start gap-2">
                <h3 className="candor-plan-card__title m-0">{copy.displayName}</h3>
                <span
                  className={`px-2 py-0.5 rounded-full text-[9px] font-extrabold uppercase tracking-wide border ${
                    tier === 'basic'
                      ? 'bg-blue-50 text-blue-600 border-blue-100'
                      : tier === 'plus'
                        ? 'bg-indigo-50 text-indigo-600 border-indigo-100'
                        : 'bg-purple-50 text-purple-600 border-purple-100'
                  }`}
                >
                  {tierBadgeLabel(tier)}
                </span>
              </div>
              <p className="candor-plan-card__tagline">{copy.tagline}</p>
              {developerTiers.length > 1 && (
                <div className="mt-3 p-1 bg-slate-100 rounded-xl flex gap-1 border border-slate-200 shadow-inner w-full">
                  {developerTiers.map((tierPlan) => {
                    const t = developerTierKey(tierPlan);
                    return (
                      <button
                        key={tierPlan.slug}
                        type="button"
                        onClick={() => onDeveloperTabChange?.(t)}
                        className={`flex-1 flex flex-col items-center py-1.5 px-1 rounded-lg text-[10px] font-bold transition-all duration-200 ${
                          developerTab === t
                            ? t === 'basic'
                              ? 'bg-blue-600 text-white shadow-md scale-[1.03]'
                              : t === 'plus'
                                ? 'bg-indigo-600 text-white shadow-md scale-[1.03]'
                                : 'bg-purple-600 text-white shadow-md scale-[1.03]'
                            : 'text-slate-500 hover:bg-white hover:shadow-sm'
                        }`}
                      >
                        {getPlanDisplayCopy(tierPlan, billingInterval).tierLabel ||
                          tierPlan.name.replace(/developer\s*/i, '').trim() ||
                          t}
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="candor-plan-card__price-row">
                <span className="candor-plan-card__price">{price.amount}</span>
                {price.suffix && (
                  <span className="candor-plan-card__price-suffix">{price.suffix}</span>
                )}
              </div>
              {copy.priceNote && <p className="candor-plan-card__desc">{copy.priceNote}</p>}
              <hr className="candor-plan-card__divider" />
              <h4 className="candor-plan-card__includes">{copy.includesLabel}</h4>
              <PlanFeatures plan={activeDevPlan} />
            </div>
            <div className="candor-plan-card__footer">
              {current ? (
                <div className="space-y-2">
                  <div className="w-full text-center py-2 bg-emerald-50 text-emerald-700 font-bold rounded-xl text-xs border border-emerald-100 shadow-sm">
                    Current Plan
                  </div>
                  {subscription?.status !== 'cancelled' && onCancelSubscription && (
                    <button
                      type="button"
                      onClick={onCancelSubscription}
                      disabled={loading.cancel}
                      className="w-full py-1.5 text-center text-xs font-semibold text-red-500 hover:text-red-700 bg-red-50/50 hover:bg-red-50 rounded-lg transition"
                    >
                      {loading.cancel ? 'Processing...' : 'Cancel Subscription'}
                    </button>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => onPlanSelect?.(slug)}
                  disabled={isLoadingSlug(slug)}
                  className={`w-full py-2.5 text-white font-bold rounded-xl text-xs transition duration-200 shadow-md hover:shadow-lg disabled:opacity-50 ${
                    tier === 'basic'
                      ? 'bg-blue-600 hover:bg-blue-700'
                      : tier === 'plus'
                        ? 'bg-indigo-600 hover:bg-indigo-700'
                        : 'bg-purple-600 hover:bg-purple-700'
                  }`}
                >
                  {isLoadingSlug(slug) ? 'Processing...' : 'Subscribe'}
                </button>
              )}
            </div>
          </div>
        );
      })()}

      {/* ***** Code Start BH29072026_1915 - Pay as you go is a monthly-only metered plan
          Paddle has no annual price for it, so the Yearly tab advertised a derived $47.90/yr
          and checkout then refused the purchase. Hide it on the Yearly tab — except when it
          is the active plan, so the Current Plan and Cancel controls stay reachable — and
          always price it monthly, the only term that actually exists. */}
      {payg && (billingInterval !== 'yearly' || isCurrent(payg.slug)) && (() => {
        const slug = payg.slug;
        const copy = getPlanDisplayCopy(payg, 'monthly');
        const price = formatPlanPriceDisplay(payg, 'monthly');
        const current = isCurrent(slug);
        return (
          <div
            key={slug}
            className={`candor-plan-card relative ${current ? 'ring-2 ring-blue-500/80 border-blue-500/50' : ''}`}
          >
            {current && <CurrentBadge />}
            <div className="candor-plan-card__body">
              <h3 className="candor-plan-card__title">{copy.displayName}</h3>
              <p className="candor-plan-card__tagline">{copy.tagline}</p>
              <div className="candor-plan-card__price-row">
                <span className="candor-plan-card__price">{price.amount}</span>
                {price.suffix && (
                  <span className="candor-plan-card__price-suffix">{price.suffix}</span>
                )}
              </div>
              {copy.priceNote && <p className="candor-plan-card__desc">{copy.priceNote}</p>}
              <hr className="candor-plan-card__divider" />
              <h4 className="candor-plan-card__includes">{copy.includesLabel}</h4>
              <PlanFeatures plan={payg} />
            </div>
            <div className="candor-plan-card__footer">
              {current ? (
                <div className="space-y-2">
                  <div className="w-full text-center py-2 bg-emerald-50 text-emerald-700 font-bold rounded-xl text-xs border border-emerald-100 shadow-sm">
                    Current Plan
                  </div>
                  {subscription?.status !== 'cancelled' && onCancelSubscription && (
                    <button
                      type="button"
                      onClick={onCancelSubscription}
                      disabled={loading.cancel}
                      className="w-full py-1.5 text-center text-xs font-semibold text-red-500 hover:text-red-700 bg-red-50/50 hover:bg-red-50 rounded-lg transition"
                    >
                      {loading.cancel ? 'Processing...' : 'Cancel Subscription'}
                    </button>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => onPlanSelect?.(slug)}
                  disabled={isLoadingSlug(slug)}
                  className="w-full py-2.5 bg-slate-800 hover:bg-slate-900 text-white font-bold rounded-xl text-xs transition duration-200 shadow-md disabled:opacity-50"
                >
                  {isLoadingSlug(slug) ? 'Processing...' : 'Subscribe'}
                </button>
              )}
            </div>
          </div>
        );
      })()}

      {(enterprise || other.length > 0) &&
        (enterprise ? [enterprise] : other).map((plan) => {
          const slug = plan.slug;
          const copy = getPlanDisplayCopy(plan, billingInterval);
          const price = formatPlanPriceDisplay(plan, billingInterval);
          const isEnterprise = classifyPlan(plan).category === 'enterprise';
          return (
            <div key={slug} className="candor-plan-card relative">
              <div className="candor-plan-card__body">
                <h3 className="candor-plan-card__title">{copy.displayName}</h3>
                <p className="candor-plan-card__tagline">{copy.tagline}</p>
                <div className="candor-plan-card__price-row">
                  <span className="candor-plan-card__price">{price.amount}</span>
                </div>
                {copy.priceNote && <p className="candor-plan-card__desc">{copy.priceNote}</p>}
                <hr className="candor-plan-card__divider" />
                <h4 className="candor-plan-card__includes">{copy.includesLabel}</h4>
                <PlanFeatures plan={plan} />
              </div>
              <div className="candor-plan-card__footer">
                {isEnterprise || price.amount === 'Custom' ? (
                  <>
                    <button
                      type="button"
                      onClick={onContactSales}
                      className="w-full py-2.5 bg-slate-800 hover:bg-slate-900 text-white font-bold rounded-xl text-xs transition duration-200 shadow-md"
                    >
                      Contact Sales
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => onPlanSelect?.(slug)}
                    disabled={isLoadingSlug(slug)}
                    className="w-full py-2.5 bg-slate-800 hover:bg-slate-900 text-white font-bold rounded-xl text-xs transition duration-200 shadow-md disabled:opacity-50"
                  >
                    {isLoadingSlug(slug) ? 'Processing...' : 'Subscribe'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
    </div>
  );
}
